import { cidFromSource } from './mailCid';
// HTML is rebuilt before entering an script-free sandboxed iframe.
// Preserve presentation, never executable markup or arbitrary resource loads.
const TAGS = new Set('p div span br hr b strong i em u s strike small big center font ul ol li blockquote pre code table caption colgroup col thead tbody tfoot tr td th h1 h2 h3 h4 h5 h6 a img sub sup'.split(' '));
const DROP = new Set('script style iframe object embed svg math form input button textarea select link meta base audio video source'.split(' '));
const ATTRS = new Set('class id title dir lang width height align valign bgcolor color face size cellpadding cellspacing border colspan rowspan span start type'.split(' '));
const PROPERTIES = /^(color|background-color|background|font(-family|-size|-weight|-style|-variant)?|line-height|letter-spacing|word-spacing|text-(align|decoration|transform|indent)|white-space|word-break|overflow-wrap|vertical-align|display|visibility|opacity|width|height|min-width|max-width|min-height|max-height|margin(-top|-right|-bottom|-left)?|padding(-top|-right|-bottom|-left)?|border(-collapse|-spacing|-radius|-(top|right|bottom|left)(-(width|style|color))?|-(width|style|color))?|table-layout|list-style-type|float|clear|box-sizing)$/i;
export function safeEmailStyle(style) {
  const result = [];
  for (let i = 0; i < style.length; i += 1) {
    const property = style[i];
    const value = style.getPropertyValue(property);
    // No CSS resource references, custom-property indirection or legacy code.
    if (!PROPERTIES.test(property) || /url\s*\(|image-set|expression|var\s*\(|attr\s*\(|[\\<>@]|\/\*/i.test(value)) continue;
    result.push(`${property}:${value}${style.getPropertyPriority(property) ? ' !important' : ''}`);
  }
  return result.join(';');
}
function safeStylesheet(css) {
  try {
    const sheet = new CSSStyleSheet();
    // Unsupported engines retain inline formatting rather than injecting raw CSS.
    if (!sheet.replaceSync) return '';
    sheet.replaceSync(css);
    const render = rules => Array.from(rules).map(rule => {
      if (rule.type === 1) {
        const declarations = safeEmailStyle(rule.style);
        return declarations ? `${rule.selectorText}{${declarations}}` : '';
      }
      const condition = rule.conditionText || rule.media?.mediaText || '';
      if (rule.type === 4 && condition && !/[{}<>@\\]/.test(condition)) return `@media ${condition}{${render(rule.cssRules)}}`;
      return '';
    }).join('');
    return render(sheet.cssRules).replace(/</g, '\\3c ');
  } catch { return ''; }
}
export function emailDocument(html, showImages = false, inlineImages = new Map()) {
  const parsed = new DOMParser().parseFromString(String(html || ''), 'text/html');
  const output = document.implementation.createHTMLDocument('Email');
  const copyPresentation = (node, element) => {
    for (const attribute of Array.from(node.attributes)) {
      if (ATTRS.has(attribute.name)) element.setAttribute(attribute.name, attribute.value);
    }
    const style = safeEmailStyle(node.style);
    if (style) element.setAttribute('style', style);
  };
  const clean = node => {
    if (node.nodeType === 3) return output.createTextNode(node.textContent);
    if (node.nodeType !== 1 || DROP.has(node.localName)) return output.createTextNode('');
    const tag = node.localName;
    if (!TAGS.has(tag)) { const fragment = output.createDocumentFragment(); Array.from(node.childNodes).forEach(child => fragment.appendChild(clean(child))); return fragment; }
    const element = output.createElement(tag);
    copyPresentation(node, element);
    if (tag === 'a') {
      const href = (node.getAttribute('href') || '').trim();
      if (/^https?:\/\//i.test(href) || /^mailto:/i.test(href)) {
        element.setAttribute('href', href); element.setAttribute('target', '_blank'); element.setAttribute('rel', 'noopener noreferrer');
      }
    }
    if (tag === 'img') {
      element.setAttribute('alt', node.getAttribute('alt') || 'Email image');
      const src = (node.getAttribute('src') || '').trim();
      const cid = cidFromSource(src);
      const localImage = cid && inlineImages.get(cid);
      if (localImage && /^blob:/i.test(localImage)) element.setAttribute('src', localImage);
      else if (showImages && /^https:\/\//i.test(src)) element.setAttribute('src', src);
    }
    Array.from(node.childNodes).forEach(child => element.appendChild(clean(child)));
    return element;
  };
  copyPresentation(parsed.body, output.body);
  Array.from(parsed.body.childNodes).forEach(node => output.body.appendChild(clean(node)));
  const css = Array.from(parsed.querySelectorAll('style')).map(style => safeStylesheet(style.textContent)).join('');
  return `<!doctype html><html><head><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src ${[showImages ? 'https:' : '', inlineImages.size ? 'blob:' : ''].filter(Boolean).join(' ') || "'none'"}; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'"><meta name="referrer" content="no-referrer"><style>body{font:14px/1.5 Arial,sans-serif;color:#222;margin:8px;overflow-wrap:anywhere}img{max-width:100%;height:auto}pre{white-space:pre-wrap}${css}</style></head>${output.body.outerHTML}</html>`;
}
